import { Color, Spacing } from '@persona-id/design-tokens';
import { TrashIcon, XIcon } from '@persona-id/design-tokens/icons';
import { startCase } from 'lodash';
import { useState } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import styled, { css } from 'styled-components';

import {
  ADDRESS_SUBFIELDS_DISPLAY_NAMES,
  NAME_SUBFIELDS_DISPLAY_NAMES,
} from 'dashboard/components/ExtractionPanel/ExtractionFieldWriteBacksPanel';
import ExtractionSingleFieldWritebackPanel from 'dashboard/components/ExtractionPanel/ExtractionSingleFieldWriteBackPanel';
import {
  ExtractionCategory,
  ExtractionPanelForm,
  FieldWriteBackPanelForm,
} from 'dashboard/components/ExtractionPanel/util';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import { IconButton } from 'design-system/components/Button';
import { ErrorText } from 'design-system/components/FormControl';
import ListBox from 'design-system/components/ListBox/ListBox';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';

const MaximizablePanelStyles = () => css`
  width: 400px;
  top: 400px;
  min-height: unset;

  #maximizable-panel__toggle-fullscreen {
    display: none;
  }
`;

const ListBoxContainer = styled.div`
  padding: ${Spacing[1]};
  border: 1px solid ${({ theme }) => theme.color.border.input};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const ListBoxItem = styled.div<{ selected: boolean }>`
  cursor: pointer;
  padding: ${Spacing[1]} ${Spacing[2]};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  color: ${({ theme, selected }) => (selected ? theme.color.variants.primary.default : 'inherit')};
  background-color: ${({ theme, selected }) =>
    selected ? theme.color.variants.primary.highlightLight : 'inherit'};
  transition-duration: ${TransitionDuration.Fast};
  user-select: none;

  &:hover {
    background-color: ${Color.CoreSlate50};
  }
`;

const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

interface ExtractionPanelAdvancedProps {
  formReturn: UseFormReturn<ExtractionPanelForm, object>;
  vtmplVersion: VerificationTemplateVersionDocument;
  disabled?: boolean;
}

// order field writebacks in subfield order
// for v3 subfields there is no way for us to tell if a particular subfield refers to name_first or name_last
// so we assume that all the subfields will be stored in name_first, name_last order for name preset and
// address_street_1, address_street_2, address_city, address_subdivision and address_postal_code order for address preset.
// the same assumption holds for field writebacks as well, so we reorder the field writebacks as the same order as
// subfields
// if the field writeback refers to multiple subfields, it will take the highest priority subfield.
const getFieldIdentifiersPriority = (
  extractionCategory: ExtractionCategory | string,
  displayName?: string,
) => {
  switch (extractionCategory) {
    case ExtractionCategory.Address: {
      const priority = ADDRESS_SUBFIELDS_DISPLAY_NAMES.indexOf(displayName || '');
      // if display name is not present, it's a field writeback that considers all subfields results as a candidates
      // for field writebacks. it's not very common or a useful field writeback, so for now just keep it at the bottom
      if (priority === -1) {
        return 5;
      }
      return priority;
    }
    case ExtractionCategory.Name: {
      const priority = NAME_SUBFIELDS_DISPLAY_NAMES.indexOf(displayName || '');
      // if display name is not present, it's a field writeback that considers all subfields results as a candidates
      // for field writebacks. it's not very common or a useful field writeback, so for now just keep it at the bottom
      if (priority === -1) {
        return 2;
      }
      return priority;
    }

    default:
      // ordering doesn't matter for non-preset field writebacks.
      return 0;
  }
};

export const orderFieldWritebackForms = (
  extractionCategory: ExtractionCategory | string,
  form1: FieldWriteBackPanelForm,
  form2: FieldWriteBackPanelForm,
) => {
  return (
    getFieldIdentifiersPriority(extractionCategory, form1.displayName) -
      getFieldIdentifiersPriority(extractionCategory, form2.displayName) ||
    form1.documentFieldKey.localeCompare(form2.documentFieldKey)
  );
};

const ExtractionMultipleFieldWriteBacksPanel = ({
  formReturn,
  vtmplVersion,
  disabled = false,
}: ExtractionPanelAdvancedProps) => {
  const { formState, watch, trigger, control } = formReturn;
  const [selectedFieldWriteBackIndex, setSelectedFieldWriteBackIndex] = useState<number | null>(
    null,
  );
  const { errors } = formState;
  const fieldWriteBackForms = watch('fieldWriteBackForms');
  const { remove } = useFieldArray({ control, name: 'fieldWriteBackForms' });

  const handleSelect = (index: number | null) => {
    setSelectedFieldWriteBackIndex(index);
    if (index == null) {
      trigger('fieldWriteBackForms');
    }
  };

  const handleClose = () => {
    handleSelect(null);
  };

  const orderedFieldWriteBackForms = fieldWriteBackForms.sort((fw1, fw2) => {
    const panelFormValues = formReturn.getValues();
    return orderFieldWritebackForms(panelFormValues.extractionCategory, fw1, fw2);
  });

  const handleDelete = () => {
    if (selectedFieldWriteBackIndex != null) {
      const selectedFieldWriteBack = orderedFieldWriteBackForms[selectedFieldWriteBackIndex];
      const unorderedFieldWriteBackIndex = fieldWriteBackForms.findIndex(
        (f) =>
          f.subfieldName === selectedFieldWriteBack.subfieldName &&
          f.documentFieldKey === selectedFieldWriteBack.documentFieldKey,
      );
      remove(unorderedFieldWriteBackIndex);
      setSelectedFieldWriteBackIndex(null);
    }
  };

  return (
    <>
      <Spacer height={1.5} />
      <div>Select where to save extractions</div>
      <Spacer height={1} />
      <div id='field-write-back-panel' style={{ pointerEvents: 'auto' }}>
        <ListBoxContainer
          style={errors?.fieldWriteBackForms ? { borderColor: Color.TextDanger } : {}}
        >
          <ListBox.Header>Document Fields</ListBox.Header>
          {orderedFieldWriteBackForms?.map((fieldWriteBackForm, index) => (
            <ListBoxItem
              key={`field-write-back-panel-list-box__list-box-item-${index}`}
              id={`field-write-back-panel-list-box__list-box-item-${index}`}
              selected={index === selectedFieldWriteBackIndex}
              onClick={
                disabled
                  ? undefined
                  : () => {
                      handleSelect(selectedFieldWriteBackIndex === index ? null : index);
                    }
              }
            >
              <div style={errors?.fieldWriteBackForms?.[index] ? { color: Color.TextDanger } : {}}>
                {fieldWriteBackForm.displayName ? (
                  <>
                    <div>{fieldWriteBackForm.displayName}</div>
                    <Paragraph size='sm'>
                      {startCase(fieldWriteBackForm.documentFieldKey) || 'No field'}
                    </Paragraph>
                  </>
                ) : (
                  <div>{startCase(fieldWriteBackForm.documentFieldKey) || 'No field'}</div>
                )}
              </div>
            </ListBoxItem>
          ))}
        </ListBoxContainer>
        <Spacer height={1} />
        {selectedFieldWriteBackIndex != null && (
          <MaximizablePanel
            title=''
            offsetElementId='extraction-panel__form'
            outsideExceptionalIdPrefixes={[
              `field-write-back-panel-list-box__list-box-item-${selectedFieldWriteBackIndex}`,
            ]}
            onOutsideClickAndEscPress={handleClose}
            getContainerStyle={MaximizablePanelStyles}
          >
            <ExtractionSingleFieldWritebackPanel
              formReturn={formReturn}
              vtmplVersion={vtmplVersion}
              fieldWriteBackFormIndex={selectedFieldWriteBackIndex}
              displayName={fieldWriteBackForms[selectedFieldWriteBackIndex].displayName}
              showClearFieldButton
              rightButton={
                <div style={{ display: 'flex' }}>
                  <IconButton id='extraction-panel-list-box__delete-button' onClick={handleDelete}>
                    <TrashIcon />
                  </IconButton>
                  <Spacer width={1} />
                  <IconButton id='extraction-panel-list-box__close-button' onClick={handleClose}>
                    <XIcon />
                  </IconButton>
                </div>
              }
            />
          </MaximizablePanel>
        )}
      </div>
      <FieldErrorText
        message={
          errors?.fieldWriteBackForms?.message ||
          (errors?.fieldWriteBackForms?.length ? 'One or more field writebacks are invalid' : '')
        }
      />
    </>
  );
};

export default ExtractionMultipleFieldWriteBacksPanel;
